import { allows } from './define';
import type { Permission, ScreenManifest } from './types';

export interface ScreenAccessEnvironment {
  readonly can: (resource: string, action: string, options?: { strict?: boolean }) => boolean;
  readonly rootTenancy: boolean;
  readonly flag: (key: string) => unknown;
  /**
   * Regra da seção da tela (a rota-mãe do legado, que o `RoutePermissionGuard` também confere).
   * Quem monta o ambiente resolve a seção pela trilha (`sectionOf`); ausente, só a folha vale.
   */
  readonly section?: (screen: ScreenManifest) => boolean;
  /**
   * Trilha da raiz até a tela (`screenTrail`): cada tela-mãe também precisa liberar, como o
   * `RoutePermissionGuard` confere todos os `matches` (R3). Ausente, só a folha e a seção valem.
   */
  readonly trail?: (screen: ScreenManifest) => readonly ScreenManifest[];
}

/** Regra própria de uma tela: `permission`, `access.anyOf`, `rootTenancy` e `flag`. */
function ownRuleAllows(screen: ScreenManifest, env: ScreenAccessEnvironment): boolean {
  const check = (permission: Permission) =>
    env.can(
      permission.resource,
      permission.action,
      permission.strict === undefined ? undefined : { strict: permission.strict },
    );
  if (!screenPermissionAllows(screen, (permission) => !permission || check(permission))) return false;
  if (screen.access?.rootTenancy && !env.rootTenancy) return false;
  const flag = screen.access?.flag;
  return (
    !flag ||
    ('equals' in flag ? env.flag(flag.key) === flag.equals : env.flag(flag.key) !== flag.notEquals)
  );
}

/**
 * Mesma permissão e condições do legado, declaradas no manifesto da rota: `permission`, `access.anyOf`,
 * `rootTenancy` e `flag` da tela **e** de cada tela-mãe da trilha, mais a regra da seção, todas juntas
 * (`RoutePermissionGuard.tsx:14-50`).
 */
export function canAccessScreen(
  screen: ScreenManifest | undefined,
  env: ScreenAccessEnvironment,
): boolean {
  if (!screen) return true;
  if (!ownRuleAllows(screen, env)) return false;
  if (env.section && !env.section(screen)) return false;
  const trail = env.trail?.(screen) ?? [];
  return trail.every((at) => at === screen || ownRuleAllows(at, env));
}

/** A própria regra do manifesto (`permission` + `access.anyOf`), sem seção nem condição. */
export function screenPermissionAllows(
  screen: ScreenManifest,
  check: (permission: Permission | undefined) => boolean,
): boolean {
  if (!check(screen.permission)) return false;
  const anyOfList = screen.access?.anyOf;
  return !anyOfList || allows({ anyOf: anyOfList }, check);
}
